import Button from 'components/Form/Button';
import { Icon } from 'components/Icon';
import { CoreContext } from 'context/CoreContext';
import { useCallback, useContext, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { UploadImage } from 'services/api';
import { Title } from 'ui/styled';
import { theme } from 'ui/theme-color';
import { exposeStrapiError } from 'utils';
import Wrapper from '../Wrapper';
import {
  AttachedFile,
  AttachedFileImage,
  AttachedFilesContainer,
  ButtonsContainer,
  FileInputContainer,
  FileLabelContainer,
  FilesContainer,
  FileTitleContainer,
} from './styled';

/**
 * @param {boolean} [props.multiple=false]
 * @param {(files: File[]) => void} props.onConfirm
 * @param {string[]} [props.acceptedFiles]
 *
 * @see Lista de tipos aceitos pelo atributo HTML `accept`.
 * @see https://developer.mozilla.org/en-US/docs/Web/HTML/Reference/Attributes/accept
 */
export const ModalUploadFile = ({ multiple, onConfirm, acceptedFiles }) => {
  const { setModal } = useContext(CoreContext);

  const inputRef = useRef(null);

  const [isDragging, setIsDragging] = useState(false);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);

  const defaultAcceptedFiles = [
    'application/pdf',
    'image/png',
    'image/jpg',
    'image/jpeg',
  ];

  const validFiles = acceptedFiles ?? defaultAcceptedFiles;

  const handleConfirm = useCallback(
    async (file) => {
      setLoading(true);
      try {
        const result = await UploadImage(file);
        if (!exposeStrapiError(result)) {
          if (onConfirm && typeof onConfirm === 'function' && result?.length) {
            onConfirm(result[0]);
          }
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    },
    [onConfirm]
  );

  const getInputValidFiles = (files) => {
    return files.join(',');
  };

  const handleDropValidateFiles = (files) => {
    let valid = true;

    files.forEach((file) => {
      if (!validFiles.includes(file.type)) {
        toast.warning(
          `Tipo de arquivo não permitido: ${file.name}.
          Tente anexar um arquivo do tipo ${getInputValidFiles(validFiles)}`
        );
        valid = false;
      }
    });

    return valid;
  };

  const onClose = () => {
    setModal(null);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);

    const areFilesValid = handleDropValidateFiles(
      Array.from(e.dataTransfer.files)
    );

    if (!areFilesValid) {
      return;
    }

    if (!multiple && e.dataTransfer.files.length > 1) {
      toast.warning('Apenas um arquivo pode ser anexado');
      return;
    }

    if (!multiple && files.length > 0) {
      setFiles([e.dataTransfer.files[0]]);
      return;
    }

    const droppedFiles = Array.from(e.dataTransfer.files);

    setFiles((prev) => [...prev, ...droppedFiles]);
  };

  const handleFileSelect = (e) => {
    if (!multiple && e.target.files.length > 0) {
      setFiles([e.target.files[0]]);
      return;
    }
    const selectedFiles = Array.from(e.target.files);

    setFiles((prev) => [...prev, ...selectedFiles]);
  };

  const convertFileSizeToMB = (bytes) => {
    return (bytes / 1024 / 1024).toFixed(2);
  };

  return (
    <Wrapper
      onClose={onClose}
      customTitle={
        <FileTitleContainer>
          <Icon name={'cloud_upload'} size={36} />
          <span>
            <Title small nomargin>
              Upload de Arquivo
            </Title>
            <p>
              Envie arquivos do seu dispositivo ou arraste e solte na área
              abaixo
            </p>
          </span>
        </FileTitleContainer>
      }
    >
      <FilesContainer>
        <FileLabelContainer
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          isDragging={isDragging}
        >
          <input
            ref={inputRef}
            type="file"
            multiple={multiple}
            style={{ display: 'none' }}
            disabled={loading}
            onChange={handleFileSelect}
            accept={getInputValidFiles(validFiles)}
          />
          <FileInputContainer>
            <span
              style={{
                textAlign: 'center',
              }}
            >
              <Icon
                name={'cloud_upload'}
                size={48}
                color={theme.palette.purple.main}
                elementAttributes={{
                  style: {
                    backgroundColor: isDragging
                      ? theme.palette.lightgrey.main
                      : 'transparent',
                  },
                }}
              />
              <Title small nomargin centred>
                Arraste e solte seus arquivos aqui
              </Title>
              <p>ou clique para selecionar arquivos</p>
            </span>
            <Button
              outline
              small
              color="purple"
              loading={loading}
              disabled={loading}
              onClick={() => inputRef?.current?.click()}
            >
              Fazer Upload
            </Button>
          </FileInputContainer>
        </FileLabelContainer>

        {files.length > 0 && (
          <AttachedFilesContainer>
            {files.map((file, index) => {
              return (
                <AttachedFile key={index}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      minWidth: 0,
                    }}
                  >
                    <AttachedFileImage file={file}>
                      {file.type.startsWith('image/') ? (
                        <Icon name={'image'} />
                      ) : file.type.startsWith('application/pdf') ? (
                        <Icon
                          name={'picture_as_pdf'}
                          color={theme.palette.white.main}
                        />
                      ) : (
                        <Icon
                          name={'unknown_document'}
                          color={theme.palette.error.main}
                          elementAttributes={{
                            style: { cursor: 'pointer' },
                          }}
                        />
                      )}
                    </AttachedFileImage>
                    <span
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        minWidth: 0,
                      }}
                    >
                      <p
                        style={{
                          margin: 0,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {file.name}
                      </p>
                      <p style={{ margin: 0 }}>
                        {convertFileSizeToMB(file.size)} MB
                      </p>
                    </span>
                  </div>

                  <Icon
                    name={'delete'}
                    elementAttributes={{ style: { cursor: 'pointer' } }}
                    onClick={() => {
                      if (!loading)
                        setFiles((file) => file.filter((_, i) => i !== index));
                    }}
                  />
                </AttachedFile>
              );
            })}
          </AttachedFilesContainer>
        )}
      </FilesContainer>
      <hr />
      <ButtonsContainer>
        <Button
          small
          color="purple"
          loading={loading}
          disabled={loading}
          onClick={async () => {
            const filesDTO = multiple ? files : files[0];

            // let uploadedFile;
            if (Array.isArray(filesDTO)) {
              // implementar upload de multiplas imagens
            } else {
              await handleConfirm(filesDTO);
            }
          }}
        >
          Fazer Upload
        </Button>
        <Button outline small onClick={onClose} color="primary">
          Cancelar
        </Button>
      </ButtonsContainer>
    </Wrapper>
  );
};

export default ModalUploadFile;
